// =============================================================================
// Availability Utils - Helper functions for resource availability
// =============================================================================

import type { Resource } from '../types';
import {
  civilDateInTz,
  civilDayOfWeek,
  minutesOfDayInTz,
} from '../lib/tenantTime';

// Mapping da JS getDay() (0=Sunday) a DayOfWeek enum del backend
const DAY_MAP: Record<number, string> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

/**
 * Interfaccia per il pattern di disponibilità
 */
export interface AvailabilityPattern {
  id: string;
  dayOfWeek: string | null;  // 'MONDAY', 'TUESDAY', etc.
  startTime: string;          // 'HH:MM' (es. '08:00')
  endTime: string;            // 'HH:MM' (es. '18:00')
  validFrom: string;
  validUntil: string | null;
  isActive: boolean;
}

/**
 * Estende il tipo Resource per includere availabilityPatterns
 */
export interface ResourceWithAvailability extends Resource {
  availabilityPatterns?: AvailabilityPattern[];
}

/**
 * Eccezione alla disponibilità (assenza o presenza aggiuntiva) rilevante per il calendario.
 * Sottoinsieme del modello backend Exception.
 */
export interface AvailabilityException {
  startDate: string;        // ISO date
  endDate: string;          // ISO date (inclusa)
  startTime: string | null; // 'HH:MM' o null = intera giornata
  endTime: string | null;
  isAvailable: boolean;     // true = presenza extra, false = assenza
  title?: string | null;    // etichetta mostrata sul calendario (opzionale)
  notes?: string | null;
  reason?: {                // causale personalizzabile (colore/icona/nome)
    id: string;
    name: string;
    color: string;
    icon: string;
  } | null;
}

/**
 * Giorno civile dell'ORGANIZZAZIONE su cui cade l'istante, come intero
 * comparabile YYYYMMDD. Leggere i campi locali darebbe il giorno del browser,
 * che per i fusi lontani puo' essere quello prima o quello dopo.
 */
function dateOnlyKey(d: Date, timeZone: string): number {
  const [y, m, day] = civilDateInTz(d, timeZone).split('-').map(Number);
  return y * 10000 + m * 100 + day;
}

function dateOnlyKeyFromISO(iso: string): number {
  const [y, m, day] = iso.slice(0, 10).split('-').map(Number);
  return y * 10000 + m * 100 + day;
}

/**
 * Verifica se un'eccezione copre lo specifico istante (data + ora) indicato.
 * Considera l'intervallo di date [startDate, endDate] e, se presenti, gli orari.
 */
function isExceptionCoveringTime(
  exc: AvailabilityException,
  date: Date,
  timeZone: string,
): boolean {
  const key = dateOnlyKey(date, timeZone);
  if (key < dateOnlyKeyFromISO(exc.startDate) || key > dateOnlyKeyFromISO(exc.endDate)) {
    return false;
  }
  // Senza orari specifici → vale per l'intera giornata
  if (!exc.startTime || !exc.endTime) {
    return true;
  }
  const currentMinutes = minutesOfDayInTz(date, timeZone);
  return currentMinutes >= timeToMinutes(exc.startTime) && currentMinutes < timeToMinutes(exc.endTime);
}

/**
 * Converte un orario HH:MM in minuti dall'inizio del giorno
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Verifica se una risorsa è operativa in un determinato momento.
 * 
 * @param resource - La risorsa con i pattern di disponibilità
 * @param date - La data/ora da verificare (include ora e minuti)
 * @returns true se la risorsa è operativa, false altrimenti
 * 
 * LOGICA:
 * 1. Calcola la disponibilità base dai pattern settimanali
 * 2. Applica le eccezioni: le presenze aggiuntive (isAvailable=true) rendono
 *    disponibile un istante altrimenti non lavorativo; le assenze (isAvailable=false)
 *    rendono indisponibile un istante altrimenti lavorativo.
 * 3. L'assenza ha la precedenza finale (non è possibile prenotare durante un'assenza).
 */
export function isResourceWorking(
  resource: ResourceWithAvailability,
  date: Date,
  exceptions: AvailabilityException[] = [],
  timeZone: string,
): boolean {
  let working = isWorkingByPattern(resource, date, timeZone);

  if (exceptions.length > 0) {
    // Presenze aggiuntive: aprono disponibilità extra
    for (const exc of exceptions) {
      if (exc.isAvailable && isExceptionCoveringTime(exc, date, timeZone)) {
        working = true;
      }
    }
    // Assenze: hanno la precedenza finale
    for (const exc of exceptions) {
      if (!exc.isAvailable && isExceptionCoveringTime(exc, date, timeZone)) {
        working = false;
      }
    }
  }

  return working;
}

/**
 * Disponibilità base derivata dai soli pattern settimanali (senza eccezioni).
 */
function isWorkingByPattern(
  resource: ResourceWithAvailability,
  date: Date,
  timeZone: string,
): boolean {
  const patterns = resource.availabilityPatterns;

  // Se non ci sono pattern, la risorsa è sempre disponibile
  // (es. veicoli, attrezzature che non hanno orari di lavoro)
  if (!patterns || patterns.length === 0) {
    return true;
  }

  // Ottieni il giorno della settimana (0 = Domenica, 1 = Lunedì, ecc.)
  const jsDay = civilDayOfWeek(civilDateInTz(date, timeZone));
  const dayOfWeek = DAY_MAP[jsDay];

  // Trova i pattern per questo giorno della settimana
  const todayPatterns = patterns.filter(p =>
    p.isActive &&
    p.dayOfWeek === dayOfWeek &&
    isPatternValidForDate(p, date, timeZone)
  );

  // Se non ci sono pattern per oggi → non lavorativo
  if (todayPatterns.length === 0) {
    return false;
  }

  // Converti l'orario corrente in minuti, nell'orologio dell'organizzazione
  const currentMinutes = minutesOfDayInTz(date, timeZone);

  // Verifica se l'orario corrente rientra in uno dei pattern
  for (const pattern of todayPatterns) {
    const startMinutes = timeToMinutes(pattern.startTime);
    const endMinutes = timeToMinutes(pattern.endTime);

    // L'orario è compreso tra start e end (incluso start, escluso end)
    if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
      return true;
    }
  }

  // Fuori da tutti gli intervalli lavorativi
  return false;
}

/**
 * Verifica se un pattern è valido per una data specifica
 * (controlla validFrom e validUntil)
 */
function isPatternValidForDate(
  pattern: AvailabilityPattern,
  date: Date,
  timeZone: string,
): boolean {
  // validFrom/validUntil sono giorni civili (colonne DATE): il confronto e' fra
  // date civili, non fra istanti.
  const checkDay = civilDateInTz(date, timeZone);

  if (checkDay < pattern.validFrom.slice(0, 10)) {
    return false;
  }

  if (pattern.validUntil && checkDay > pattern.validUntil.slice(0, 10)) {
    return false;
  }
  
  return true;
}

/**
 * Ottiene tutti gli intervalli lavorativi di una risorsa per una data specifica
 * Utile per visualizzare le fasce orarie lavorative nella griglia
 * 
 * @returns Array di intervalli { startMinutes, endMinutes }
 */
export function getWorkingIntervals(
  resource: ResourceWithAvailability,
  date: Date,
  timeZone: string
): Array<{ startMinutes: number; endMinutes: number }> {
  const patterns = resource.availabilityPatterns;
  
  if (!patterns || patterns.length === 0) {
    // Nessun pattern = sempre disponibile (8-20 come default griglia)
    return [{ startMinutes: 8 * 60, endMinutes: 20 * 60 }];
  }
  
  const jsDay = civilDayOfWeek(civilDateInTz(date, timeZone));
  const dayOfWeek = DAY_MAP[jsDay];
  
  const todayPatterns = patterns.filter(p => 
    p.isActive && 
    p.dayOfWeek === dayOfWeek &&
    isPatternValidForDate(p, date, timeZone)
  );
  
  return todayPatterns.map(p => ({
    startMinutes: timeToMinutes(p.startTime),
    endMinutes: timeToMinutes(p.endTime),
  }));
}
